import { AxiosResponse } from "axios";
import * as React from "react";
import { CloudDomainService, IDomainDto } from "../../../../services/cloud-domain-service";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import { KuikaListTableNameCell, KuikaListTableStatusCell } from "../../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../../ui-kit/kuika-table/kuika-table";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import AddDomainModal from "./add-domain-modal";
import "./cloud-domains.scss";
import { getDomainStatusPresentation, IDomainStatusPresentation } from "./domain-status";

interface DomainRow {
  id: string;
  domain: string;
  status: string;
  dnsCnameHost?: string;
  dnsCnameTarget?: string;
}

interface CloudDomainsState {
  showAddDomain: boolean;
  loading: boolean;
  error?: string;
  domains: DomainRow[];
  domainToDelete?: DomainRow;
  /** The domain the table should walk to and light up after a verify, or null. */
  revealDomainId: string | null;
}

class CloudDomainsBase extends React.Component<IWithHistoryProp, CloudDomainsState> {
  private addDomainModalRef = React.createRef<AddDomainModal>();

  constructor(props: IWithHistoryProp) {
    super(props);
    this.state = {
      showAddDomain: false,
      loading: false,
      error: undefined,
      domains: [],
      domainToDelete: undefined,
      revealDomainId: null
    };
  }

  componentDidMount() {
    this.fetchDomains();
  }

  fetchDomains = async (): Promise<void> => {
    this.setState({ loading: true, error: undefined });

    try {
      const response: AxiosResponse<IDomainDto[]> = await CloudDomainService.GetDomains();

      if (response && response.data) {
        const domains: DomainRow[] = response.data.map((domain: IDomainDto) => ({
          id: domain.id,
          domain: domain.domain,
          status: domain.status,
          dnsCnameHost: domain.dnsCnameHost,
          dnsCnameTarget: domain.dnsCnameTarget
        }));
        this.setState({ domains, loading: false });
      } else {
        throw new Error("No data received from domain service");
      }
    } catch (error: unknown) {
      console.error("Error fetching domains:", error);
      this.setState({
        error: error instanceof Error ? error.message : "Failed to fetch domains",
        loading: false
      });
    }
  };

  handleOpenAddDomain = () => {
    this.setState({ showAddDomain: true });
  };

  handleCloseAddDomain = () => {
    this.setState({ showAddDomain: false });
    // Reset modal state after closing
    if (this.addDomainModalRef.current) {
      this.addDomainModalRef.current.resetState();
    }
    // Refresh domains list to show any changes
    this.fetchDomains();
  };

  handleSaveAddDomain = (domainId: string): void => {
    this.setState({ showAddDomain: false, revealDomainId: domainId });
    this.fetchDomains();
  };

  handleDomainRevealed = (): void => {
    this.setState({ revealDomainId: null });
  };

  handleDomainRowClick = (row: DomainRow) => {
    const { navigator } = this.props;
    const currentDomain = navigator.getCurrentDomain();

    if (!currentDomain) {
      return;
    }

    navigator.gotoUrl(`/${currentDomain}/cloud/domains/${encodeURIComponent(row.id)}`);
  };

  handleDeleteClick = (row: DomainRow) => {
    this.setState({ domainToDelete: row });
  };

  handleCancelDelete = () => {
    this.setState({ domainToDelete: undefined });
  };

  // DeleteModal owns the in-flight state and closes itself once this settles, so a failure
  // is reported on the page rather than thrown back into the dialog.
  handleConfirmDelete = async (): Promise<void> => {
    const { domainToDelete } = this.state;
    if (!domainToDelete) return;

    this.setState({ error: undefined });

    try {
      await CloudDomainService.DeleteDomain(domainToDelete.id);
      this.fetchDomains();
    } catch (error: unknown) {
      console.error("Error deleting domain:", error);
      this.setState({
        error: error instanceof Error ? error.message : "Failed to delete domain"
      });
    }
  };

  private readonly columns: KuikaTableColumn<DomainRow>[] = [
    {
      id: "domain",
      accessorKey: "domain",
      enableSorting: true,
      header: () => "Domain",
      cell: (row: DomainRow) => (
        <KuikaListTableNameCell name={row.domain} icon={<KuikaSvg name="world-www" width={12} height={12} />} />
      )
    },
    {
      id: "status",
      accessorFn: (row: DomainRow): number => getDomainStatusPresentation(row.status).sortOrder,
      enableSorting: true,
      header: () => "Status",
      cell: (row: DomainRow) => {
        const presentation: IDomainStatusPresentation = getDomainStatusPresentation(row.status);
        return (
          <span className="kuika-flex-between kuika-width-full">
            {/* A verify, a refresh or the provisioning finishing moves a row's status while the
                list is open, so the badge morphs to the new one. */}
            <KuikaListTableStatusCell label={presentation.label} color={presentation.color} animateChanges />
            <span className="kuika-cloud-domains__actions">
              <KuikaDropdownMenu
                options={[
                  {
                    label: "Delete",
                    value: "delete",
                    icon: <KuikaSvg name="trash" width={16} height={16} />,
                    danger: true,
                    onClick: () => this.handleDeleteClick(row)
                  }
                ]}
              >
                <KuikaButton type="tertiary" size="sm" isIcon color="neutral" aria-label={`${row.domain} actions`}>
                  <KuikaSvg name="dots" width={16} height={16} pointer />
                </KuikaButton>
              </KuikaDropdownMenu>
            </span>
          </span>
        );
      },
      noWrap: true
    }
  ];

  render() {
    const { showAddDomain, loading, error, domains, domainToDelete, revealDomainId } = this.state;

    return (
      <div className="kuika-settings-panel">
        <div className="kuika-settings-panel__header">
          <div className="kuika-settings-panel__header-left">
            <KuikaSvg width={24} height={24} name="world-www" />
            <KuikaH4>Domains</KuikaH4>
          </div>
          <div className="kuika-settings-panel__header-right">
            <KuikaButton type="primary" onClick={this.handleOpenAddDomain}>
              <KuikaSvg name="plus" width={16} height={16} />
              ADD DOMAIN
            </KuikaButton>
          </div>
        </div>
        <div className="kuika-cloud-domains__content">
          {error && (
            <KuikaInfoBox
              color="red"
              leftUnit={<KuikaText>{error}</KuikaText>}
              rightUnit={
                <KuikaButton type="secondary" size="sm" onClick={this.fetchDomains}>
                  RETRY
                </KuikaButton>
              }
            />
          )}
          {!error && (
            <KuikaTable
              data={domains}
              columns={this.columns}
              rowKey={(row: DomainRow) => row.id}
              pageSize={5}
              showCountLabel="Domains"
              enableSorting
              initialState={{
                sorting: [{ id: "status", desc: false }]
              }}
              onRowClick={this.handleDomainRowClick}
              loading={loading}
              revealRowKey={revealDomainId}
              onRowRevealed={this.handleDomainRevealed}
              emptyIcon={<KuikaSvg name="world-www" width={48} height={48} color="var(--dark-5)" />}
              emptyTitle="No domains yet"
              emptyDescription="Add your first custom domain to get started"
            />
          )}
        </div>
        <AddDomainModal
          ref={this.addDomainModalRef}
          show={showAddDomain}
          onClose={this.handleCloseAddDomain}
          onSave={this.handleSaveAddDomain}
        />
        <DeleteModal
          itemName={domainToDelete?.domain ?? ""}
          extraText="domain? This cannot be undone."
          triggerModal={!!domainToDelete}
          deleteItem={this.handleConfirmDelete}
          deleteType="domain"
          closeModal={this.handleCancelDelete}
        />
      </div>
    );
  }
}

const CloudDomains = withManagement(withHistory(CloudDomainsBase));
export { CloudDomains };
